import { API_BASE, apiFetch, uploadWithToken } from './core.js';
import { SESSION_TIMEOUT_MS } from '../lib/analytics.js';

export const customerAuth = {
  login: (email, password, turnstileToken) => apiFetch('/customer/auth/login', { method: 'POST', body: JSON.stringify({ email, password, turnstileToken }) }),
  register: (data) => apiFetch('/customer/auth/register', { method: 'POST', body: JSON.stringify(data) }),
  me: () => apiFetch('/customer/auth/me'),
  logout: () => apiFetch('/customer/auth/logout', { method: 'POST' }),
};

export const publicApi = {
  products: (params = {}) => {
    const q = new URLSearchParams();
    if (params.q) q.set('q', params.q);
    if (params.categoryId) q.set('categoryId', params.categoryId);
    if (params.featured !== undefined) q.set('featured', params.featured);
    if (params.lastChance !== undefined) q.set('lastChance', params.lastChance);
    if (params.upcoming !== undefined) q.set('upcoming', params.upcoming);
    if (params.petition !== undefined) q.set('petition', params.petition);
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.limit) q.set('limit', params.limit);
    return apiFetch(`/public/products?${q.toString()}`);
  },
  product: (slug) => apiFetch(`/public/products/${encodeURIComponent(slug)}`),
  productProgress: (slug) => apiFetch(`/public/products/${encodeURIComponent(slug)}/progress`),
  categories: () => apiFetch('/public/categories'),
  creators: () => apiFetch('/public/creators'),
  shippingMethods: () => apiFetch('/public/shipping-methods'),
  subscribeNewsletter: (data) => apiFetch('/public/newsletter/subscribe', { method: 'POST', body: JSON.stringify(data) }),
  createTicket: (data) => apiFetch('/support', { method: 'POST', body: JSON.stringify(data) }),
  signPetition: (productId) => apiFetch(`/public/products/${productId}/petition`, { method: 'POST' }),
  petitionStatus: (productId) => apiFetch(`/public/products/${productId}/petition/status`),
};

function getAnalyticsSessionId() {
  const key = 'miskipop_session_id';
  const lastActivityKey = 'miskipop_session_last_activity';
  let sessionId = sessionStorage.getItem(key);
  const lastActivity = Number(sessionStorage.getItem(lastActivityKey) || 0);
  if (!sessionId || !lastActivity || Date.now() - lastActivity >= SESSION_TIMEOUT_MS) {
    sessionId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    sessionStorage.setItem(key, sessionId);
  }
  sessionStorage.setItem(lastActivityKey, String(Date.now()));
  return sessionId;
}

function getAnalyticsVisitorId() {
  const key = 'miskipop_visitor_id';
  let visitorId = localStorage.getItem(key);
  if (!visitorId) {
    visitorId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(key, visitorId);
  }
  return visitorId;
}

const ANALYTICS_QUEUE_KEY = 'miskipop_analytics_queue_v1';
const ANALYTICS_QUEUE_LIMIT = 250;
let analyticsFlushPromise = null;

function readAnalyticsQueue() {
  try {
    const value = JSON.parse(localStorage.getItem(ANALYTICS_QUEUE_KEY) || '[]');
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function writeAnalyticsQueue(queue) {
  try {
    localStorage.setItem(ANALYTICS_QUEUE_KEY, JSON.stringify(queue.slice(-ANALYTICS_QUEUE_LIMIT)));
  } catch {
    // Storage may be unavailable in private browsing; the live request still runs.
  }
}

function enqueueAnalyticsEvent(payload) {
  writeAnalyticsQueue([...readAnalyticsQueue(), payload]);
}

function isPermanentAnalyticsError(error) {
  return Number(error?.status) >= 400 && Number(error?.status) < 500 && Number(error?.status) !== 429;
}

async function flushAnalyticsQueue() {
  if (analyticsFlushPromise) return analyticsFlushPromise;
  analyticsFlushPromise = (async () => {
    const pending = readAnalyticsQueue();
    const remaining = [];
    for (let index = 0; index < pending.length; index += 1) {
      const payload = pending[index];
      try {
        await apiFetch('/public/analytics/events', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      } catch (error) {
        if (isPermanentAnalyticsError(error)) continue;
        remaining.push(...pending.slice(index));
        break;
      }
    }
    writeAnalyticsQueue([...remaining, ...readAnalyticsQueue().slice(pending.length)]);
  })().finally(() => {
    analyticsFlushPromise = null;
  });
  return analyticsFlushPromise;
}

function flushAnalyticsWithBeacon() {
  if (typeof navigator?.sendBeacon !== 'function') return;
  const pending = readAnalyticsQueue().slice(0, 20);
  const sent = [];
  for (const payload of pending) {
    const body = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    if (!navigator.sendBeacon(`${API_BASE}/public/analytics/events`, body)) break;
    sent.push(payload.clientEventId);
  }
  if (sent.length) {
    writeAnalyticsQueue(readAnalyticsQueue().filter((payload) => !sent.includes(payload.clientEventId)));
  }
}

export const analytics = {
  track: async (event) => {
    const clientEventId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const payload = {
      sessionId: getAnalyticsSessionId(),
      visitorId: getAnalyticsVisitorId(),
      ...event,
      clientEventId,
      metadata: {
        ...(event.metadata || {}),
        clientEventId,
        userAgent: navigator.userAgent,
        screen: `${window.screen.width}x${window.screen.height}`,
        language: navigator.language,
        connectionType: navigator.connection?.effectiveType || null,
      },
    };
    try {
      const result = await apiFetch('/public/analytics/events', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      void flushAnalyticsQueue();
      return result;
    } catch {
      enqueueAnalyticsEvent(payload);
      return null;
    }
  },
};

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => void flushAnalyticsQueue());
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void flushAnalyticsQueue();
  });
  window.addEventListener('pagehide', flushAnalyticsWithBeacon);
  window.setInterval(() => void flushAnalyticsQueue(), 30_000);
}

export const cart = {
  get: () => apiFetch('/cart'),
  addItem: async (productId, variantId, quantity) => {
    const payload = { productId, quantity };
    if (variantId) payload.variantId = variantId;
    const result = await apiFetch('/cart/items', { method: 'POST', body: JSON.stringify(payload) });
    analytics.track({ eventType: 'ADD_TO_CART', entityType: 'PRODUCT', entityId: productId, productId, metadata: { quantity, variantId: variantId || null, path: window.location.pathname } });
    return result;
  },
  updateItem: (itemId, quantityOrPayload) => {
    const payload = typeof quantityOrPayload === 'object' && quantityOrPayload !== null ? quantityOrPayload : { quantity: quantityOrPayload };
    return apiFetch(`/cart/items/${itemId}`, { method: 'PATCH', body: JSON.stringify(payload) });
  },
  removeItem: (itemId) => apiFetch(`/cart/items/${itemId}`, { method: 'DELETE' }),
  clear: () => apiFetch('/cart/clear', { method: 'DELETE' }),
  merge: (token) => apiFetch('/cart/merge', { method: 'POST', body: JSON.stringify({ token }) }),
};

export const customerPanel = {
  getProfile: () => apiFetch('/customer/panel/profile'),
  updateProfile: (data) => apiFetch('/customer/panel/profile', { method: 'PATCH', body: JSON.stringify(data) }),
  updatePassword: (data) => apiFetch('/customer/panel/password', { method: 'PATCH', body: JSON.stringify(data) }),
  updateEmail: (data) => apiFetch('/customer/panel/email', { method: 'PATCH', body: JSON.stringify(data) }),
  getOrders: () => apiFetch('/customer/panel/orders'),
  getOrder: (id) => apiFetch(`/customer/panel/orders/${id}`),
  uploadAvatar: (file) => uploadWithToken('/customer/panel/upload', file, 'avatars', 'customer_token'),
};

export const checkoutApi = {
  status: (statusToken) => apiFetch('/checkout/status', { method: 'POST', body: JSON.stringify({ statusToken }) }),
  create: async (data) => {
    let capabilities;
    try { capabilities = await apiFetch('/checkout/capabilities', { cache: 'no-store' }); } catch { /* handled by the capability guard below */ }
    if (capabilities?.item?.durableCheckoutVersion !== 2) {
      throw new Error('Checkout en actualización. Recarga la página e inténtalo más tarde; no se ha iniciado otro pago.');
    }
    analytics.track({ eventType: 'CHECKOUT_START', entityType: 'CHECKOUT', entityId: window.location.pathname, metadata: { path: window.location.pathname } });
    // An old node cannot route this to its non-idempotent POST /checkout handler.
    return apiFetch('/checkout/v2', { method: 'POST', body: JSON.stringify(data) });
  }
};

export const coupons = {
  validate: (data) => apiFetch('/public/coupons/validate', { method: 'POST', body: JSON.stringify(data) }),
  available: () => apiFetch('/public/coupons/available')
};

export const influencerPanel = {
  metrics: () => apiFetch('/influencers/panel/metrics'),
  orders: () => apiFetch('/influencers/panel/orders'),
  comments: () => apiFetch('/influencers/panel/comments'),
  updateComment: (commentId, status) => apiFetch(`/influencers/panel/comments/${commentId}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  getProfile: () => apiFetch('/influencers/panel/profile'),
  updateProfile: (data) => apiFetch('/influencers/panel/profile', { method: 'PATCH', body: JSON.stringify(data) }),
  upload: (file, folder = 'avatars') => uploadWithToken('/influencers/panel/upload', file, folder),
  documents: () => apiFetch('/documents'),
  viewDocument: (id) => apiFetch(`/documents/${id}/view`, { method: 'POST' }),
  uploadSignedDocument: (id, file) => uploadWithToken(`/documents/${id}/signed`, file, 'contracts', 'admin_token'),
  signDocument: (id, signatureData, signatureName) => apiFetch(`/documents/${id}/sign`, { method: 'POST', body: JSON.stringify({ signatureData, signatureName, consent: true }) }),
  downloadDocument: (id, kind = 'original') => `${API_BASE}/documents/${id}/download/${kind}`,
};
